'use strict';

import { Component } from 'react';
import PropTypes from 'prop-types';
import Modal from 'react-modal';
import moment from 'moment';
import UserActions from '../../actions/UserActions';
import MealStore from '../../stores/MealStore';
import Analytics from '@utils/Analytics';

import uuid from 'uuid';
import { isEqual } from 'lodash';

import { convertWeight, roundToDecimals, formatWeight, prepareWeightForSave } from '../../utils/Math';
import WeightInput from '../Input/WeightInput.react';

import './EditDailyLog.scss';

export default class EditWeight extends Component {

    static propTypes = {
        user: PropTypes.object,
        meals: PropTypes.array,
        onModifyMeals: PropTypes.func,
        dateFormatted: PropTypes.string,
        date: PropTypes.object,
        text: PropTypes.string,
    }

    static defaultProps = {
        dateFormatted: 'Today, ' + moment().format('MMM Do'),
        date: moment(),
    }

    constructor(props) {
        super(props);

        const {weight_kg, weight_lbs} = this.getWeightFromMeals(props);

        this.state = {
            isModalOpen: false,
            weight_kg: weight_kg,
            weight_lbs: weight_lbs,
            displayError: false,
            weightHasFocus: false,
        };

    }

    componentDidUpdate(prevProps) {
        const { meals } = this.props;

        if (!isEqual(prevProps.meals, meals)) {
            let sameDay = !meals.length && !prevProps.meals.length;
            const prevDate = prevProps.meals[0] && prevProps.meals[0].date;
            const nextDate = meals[0] && meals[0].date;
    
            if(prevDate && nextDate) {
                sameDay = moment(prevDate).isSame(moment(nextDate), 'day');
            }
    
            if (!sameDay) {
                const {weight_kg, weight_lbs} = this.getWeightFromMeals(this.props);
                this.setState({weight_kg, weight_lbs});
            }
        }
    }

    getWeightFromMeals = (props) => {
        const { date, meals, user } = props;

        let weight_kg = null, weight_lbs = null;

        meals.forEach(meal => {
            if (meal.weight_kg && weight_kg === null) {
                weight_kg = meal.weight_kg;
            }
            if (meal.weight_lbs && weight_lbs === null) {
                weight_lbs = meal.weight_lbs;
            }
        });

        // Fallback to user's current weight if no weight in meals
        if (weight_kg === null && user?.weight_kg) {
            weight_kg = user.weight_kg;
        }
        if (weight_lbs === null && user?.weight_lbs) {
            weight_lbs = user.weight_lbs;
        }

        weight_kg = weight_kg ? roundToDecimals(weight_kg, 1) : '';
        weight_lbs = weight_lbs ? roundToDecimals(weight_lbs, 1) : '';

        const allMeals = MealStore.getMeals().filter(m => m.meal_type === "note" && m.weight_kg && moment(m.date).isBefore(moment(date), 'day')).sort((a,b) => moment(a.date).diff(moment(b.date)));

        const previousEntry = allMeals && allMeals.length ? convertWeight(allMeals[allMeals.length - 1].weight_kg, 'kg', 'lbs', 1) : 0;
        const previousDate = allMeals && allMeals.length ? moment(allMeals[allMeals.length - 1].date).format("dddd, MMMM Do YYYY") : null;

        return { weight_kg, weight_lbs, previousEntry, previousDate };
    }

    openWeightEditorModal = () => {
        const {weight_kg, weight_lbs} = this.getWeightFromMeals(this.props);
        this.setState({isModalOpen: true, weight_kg, weight_lbs});
    }

    closeModal = () => {
        this.setState({isModalOpen: false});
    }

    saveWeight = () => {
        const { date, meals, user, onModifyMeals } = this.props;
        const { weight_kg, weight_lbs } = this.state;

        const isEnglish = user.units_mode === 'english';
        const inputValue = isEnglish ? weight_lbs : weight_kg;
        const weightValues = prepareWeightForSave(inputValue, user.units_mode);

        const userCurrentWeight = isEnglish ? user.weight_lbs : user.weight_kg;
        const newWeight = isEnglish ? weightValues.weight_lbs : weightValues.weight_kg;

        if (date.isSame(moment(), 'day') && userCurrentWeight != newWeight) {
            UserActions.updateSpecificMeta(weightValues);
        }

        // Are there no note meals? Create one.
        if (!meals.length) {
            onModifyMeals([{
                uuid: uuid.v4(),
                meal_type: 'note',
                meal: 'daily',
                date: date.format('YYYY-MM-DD'),
                ...weightValues,
                source: 'user',
            }]);

            this.closeModal();
            return;
        }

        const meal = meals[0];

        meal.weight_kg = weightValues.weight_kg;
        meal.weight_lbs = weightValues.weight_lbs;
        meal.source = 'user';

        onModifyMeals([meal], false, false, meals.filter(m => m !== meal));

        Analytics.logCurrentWeight();

        this.closeModal();
    }

    onChangeWeight = ({ weight_kg, weight_lbs }) => {
        const isZero = weight_kg === 0 || weight_kg === '0';
        this.setState({ weight_kg, weight_lbs, displayError: isZero });
    }

    renderModal = () => {
        const { user, dateFormatted } = this.props;
        const { isModalOpen, weight_kg, weight_lbs, displayError, weightHasFocus} = this.state;

        if (!isModalOpen) {
            return;
        }

        return (
            <Modal isOpen={true}
                className="edit-timings-modal"
                overlayClassName="feed-modal-overlay"
                onRequestClose={this.closeModal}
                closeModal={this.closeModal}
                contentLabel="Edit recipe timings"
                closeTimeoutMS={250}>
                <div className="edit-daily-log-modal-container">
                    <header className="modal-header">
                        <button className="close-btn" onClick={this.closeModal}>
                            <i className="icon-close-x" />
                        </button>
                    </header>
                    <div className="edit-daily-log-modal-body daily-log-extras edit-weight-modal">
                        <h2>WEIGHT ENTRY</h2>
                        <label className="date-label">{dateFormatted}</label>
                        <div className="with-units">
                            <WeightInput
                                valueKg={weight_kg}
                                valueLbs={weight_lbs}
                                unitsMode={user?.units_mode}
                                placeholder={weightHasFocus ? "" : "Enter weight"}
                                onChange={this.onChangeWeight}
                                onFocus={() => this.setState({weightHasFocus: true})}
                                onBlur={() => this.setState({weightHasFocus: false})}
                            />
                        </div>
                        {displayError ? <p className="error_msg">Please enter a valid weight.</p> : null}
                    </div>

                    <footer>
                        <button className="cancel-btn" onClick={this.closeModal}>cancel</button>
                        <button className="save-btn" onClick={this.saveWeight}>save</button>
                    </footer>
                </div>
            </Modal>
        );
    }

    renderDailyLogSection = () => {
        const { user, meals} = this.props;
        const { previousEntry, previousDate, weight_kg, weight_lbs} = this.getWeightFromMeals(this.props);
        const { isModalOpen} = this.state;

        return (
                <section className="with-header biometrics">
                    <header><h3>Biometrics</h3></header>

                    {user && user.units_mode === 'metric' ?
                        <div className="with-units">
                            <span>
                                <label onClick={parseInt(weight_kg || 0) === 0 ? this.openWeightEditorModal : null}> {weight_kg || "- -"}</label>
                                <label>kg</label>
                            </span>

                            {weight_kg
                                ? <button className="edit-link-button"  onClick={this.openWeightEditorModal}>EDIT</button>
                                : <button className="add-notes-btn"  onClick={this.openWeightEditorModal}>Record Weight</button>
                            }
                            {!weight_kg && previousEntry && previousDate ?
                                (   <div className="previous-entry">
                                        <p>Last recorded:</p>
                                        <div>
                                            <p>{previousEntry} Ibs</p>
                                            <p>{previousDate}</p>
                                        </div>
                                    </div>
                                )
                            : null}
                        </div>
                    : null}

                    {user && user.units_mode === 'english' ?
                        <div className="with-units">
                            <span>
                                <label onClick={parseInt(weight_kg || 0) === 0 ? this.openWeightEditorModal : null}> {weight_lbs || "- -"}</label>
                                <label>lbs</label>
                            </span>
                            {weight_lbs
                                ? <button className="edit-link-button"  onClick={this.openWeightEditorModal}>EDIT</button>
                                : <button className="add-notes-btn"  onClick={this.openWeightEditorModal}>Record Weight</button>
                            }
                            {!weight_lbs && previousEntry && previousDate ?
                                (   <div className="previous-entry">
                                        <p>Last recorded:</p>
                                        <div>
                                            <p>{previousEntry} Ibs</p>
                                            <p>{previousDate}</p>
                                        </div>
                                    </div>
                                )
                            : null}
                        </div>
                    : null}
                    {this.renderModal()}
                </section>
        )
    }

    render = () => {
        const { text } = this.props;

        return text ?
            <button
                className="el-btn el-small-btn el-raspberry-outline-btn el-medium-wide-btn"
                onClick={this.openWeightEditorModal}
            >
                {text}
                <span onClick={(event) => event.stopPropagation()}>
                    {this.renderModal()}
                </span>
            </button>
            : this.renderDailyLogSection();
    }

}
